// Anliegen zwischen Bestell-App und Merxo, Abos und Einwilligungen – reine Funktionen für Server UND Oberfläche
// (docs/integration/bestell-app.md §20, S5, S6, S8, S9). Keine Datenbank, keine Uhr.
import { addDays, type IsoDate } from '@shared/time'
import { z } from 'zod'

// ---- Arten und Zustände -----------------------------------------------------------------------------------------------------------

/** Aus der App: Geschäftskunden-Antrag und Löschantrag; Merxo entscheidet. */
export const APP_CASE_KINDS = ['business_account', 'account_deletion'] as const
/** Aus Merxo: Aufträge an die App. */
export const MERXO_CASE_KINDS = [
  'link_account',
  'revoke_offers_push',
  'pause_subscription',
  'resume_subscription',
] as const
/** Aus dem Telefonassistenten (docs/integration/telefonassistent.md §6): Interessent; Ordaneo entscheidet und schließt selbst ab. */
export const PHONE_CASE_KINDS = ['lead'] as const
/** Aus dem E-Mail-Eingang (Anfragen-Zentrale, docs/anfragen-zentrale/SPEZIFIKATION.md §5): Ordaneo ordnet ein, das Personal schließt ab. */
export const MAIL_CASE_KINDS = ['mail'] as const
/** Arten, die die Bestell-App kennt (ihre Kanal-Verträge; ohne die des Telefonassistenten) */
export const CHANNEL_APP_CASE_KINDS = [...APP_CASE_KINDS, ...MERXO_CASE_KINDS] as const
export const CASE_KINDS = [...APP_CASE_KINDS, ...MERXO_CASE_KINDS, ...PHONE_CASE_KINDS, ...MAIL_CASE_KINDS] as const
export type CaseKind = (typeof CASE_KINDS)[number]
export type AppCaseKind = (typeof APP_CASE_KINDS)[number]
export type MerxoCaseKind = (typeof MERXO_CASE_KINDS)[number]
/** Herkunft eines Anliegens: Bestell-App, Ordaneo selbst (Auftrag an die App) oder Telefonassistent */
export const CASE_ORIGINS = ['app', 'merxo', 'phone', 'mail'] as const
export type CaseOrigin = (typeof CASE_ORIGINS)[number]

export const CASE_STATUSES = ['open', 'accepted', 'rejected', 'done', 'failed'] as const
export type CaseStatus = (typeof CASE_STATUSES)[number]

export const CASE_KIND_LABELS: Record<CaseKind, string> = {
  business_account: 'Geschäftskunden-Antrag',
  account_deletion: 'Löschantrag',
  link_account: 'Konto zuordnen',
  revoke_offers_push: 'Widerruf der Werbe-Mitteilungen',
  pause_subscription: 'Abo pausieren',
  resume_subscription: 'Abo fortsetzen',
  lead: 'Interessent (Telefon)',
  mail: 'E-Mail',
}

export const isAppKind = (kind: string): kind is AppCaseKind => (APP_CASE_KINDS as readonly string[]).includes(kind)

/** Zustandsmarke je Herkunft: Ein Auftrag aus Merxo „wartet auf die App“, ein Antrag der App „wartet auf Entscheidung“. */
export function caseStatusLabel(origin: CaseOrigin, status: CaseStatus): string {
  // Interessent: Ordaneo entscheidet und schließt selbst ab (TA-D10)
  if (origin === 'phone') return status === 'open' ? 'Neu – bitte melden' : status === 'failed' ? 'Verworfen' : 'Erledigt'
  // E-Mail: Ordaneo ordnet ein, das Personal verarbeitet weiter und schließt ab
  if (origin === 'mail') return status === 'open' ? 'Neu – bitte prüfen' : status === 'failed' ? 'Verworfen' : 'Erledigt'
  switch (status) {
    case 'open':
      return origin === 'app' ? 'Wartet auf Entscheidung' : 'Wartet auf die Bestell-App'
    case 'accepted':
      return 'Angenommen – die App führt aus'
    case 'rejected':
      return 'Abgelehnt – die App informiert'
    case 'done':
      return 'Erledigt'
    case 'failed':
      return 'Fehlgeschlagen'
  }
}

/** Offen heißt: Es ist noch etwas zu tun – in Merxo (Entscheidung) oder in der App (Ausführung). */
export const isOpenStatus = (status: CaseStatus): boolean =>
  status === 'open' || status === 'accepted' || status === 'rejected'

// ---- Nutzlast je Art (Form der App, docs/MERXO.md 13.8) -----------------------------------------------------------------------------

const text = (max: number) => z.string().trim().max(max)

/** Geschäftskunden-Antrag der App: nur Text, nur was für die Entscheidung nötig ist (S9). Unbekannte Angaben werden abgelehnt. */
export const zBusinessPayload = z.strictObject({
  companyName: text(200).min(1, { error: 'Der Firmenname fehlt.' }),
  contactName: text(200).min(1, { error: 'Die Ansprechperson fehlt.' }),
  email: z.email({ error: 'Die E-Mail-Adresse ist ungültig.' }).max(200),
  phone: text(60).optional(),
  segment: text(40).optional(),
  street: text(200).optional(),
  zip: text(20).optional(),
  city: text(120).optional(),
  vatId: text(30).optional(),
  message: text(1000).optional(),
})

export const zDeletionPayload = z.strictObject({
  email: z.email({ error: 'Die E-Mail-Adresse ist ungültig.' }).max(200),
  customerNumber: text(40).optional(),
  note: text(500).optional(),
})

export type BusinessPayload = z.output<typeof zBusinessPayload>
export type DeletionPayload = z.output<typeof zDeletionPayload>

/** Art des Interessenten laut Anrufer (Spezifikation Vocaris §7.1) */
export const LEAD_CUSTOMER_TYPES = ['private', 'business', 'event', 'unknown'] as const
export const LEAD_CUSTOMER_TYPE_LABELS: Record<(typeof LEAD_CUSTOMER_TYPES)[number], string> = {
  private: 'Privat',
  business: 'Firma',
  event: 'Fest oder Veranstaltung',
  unknown: 'unbekannt',
}
/** Herkunft der Rückrufnummer: vom Anrufer bestätigt oder nur die (fälschbare) Anrufer-Kennung (TA-R20) */
export const LEAD_PHONE_SOURCES = ['confirmed', 'caller_id'] as const
/** Ergebnis eines Interessenten für den Telefonassistenten (`sales.phone.cases`, ohne Kundennummer) */
export const LEAD_OUTCOMES = ['customer_created', 'contacted', 'discarded', 'expired'] as const
export type LeadOutcome = (typeof LEAD_OUTCOMES)[number]

const isoInstant = z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' })

/**
 * Interessent aus dem Telefonassistenten (Spezifikation Vocaris §7.1): nur Text, nur diese Felder (unbekannte → 400). Die Einwilligung
 * `contactConsent` prüft der Vertrag gesondert (422 `consent_missing`, wenn sie nicht `yes` ist).
 */
export const zLeadPayload = z.strictObject({
  name: text(120).min(1, { error: 'Der Name fehlt.' }),
  phone: z.string().trim().regex(/^\+[1-9][0-9]{6,14}$/, { error: 'Die Rückrufnummer hat die Form +49… (E.164).' }),
  phoneSource: z.enum(LEAD_PHONE_SOURCES, { error: 'Die Herkunft der Nummer ist confirmed oder caller_id.' }),
  company: text(120).optional(),
  email: z.email({ error: 'Die E-Mail-Adresse ist ungültig.' }).max(200).optional(),
  street: text(120).optional(),
  zip: z.string().trim().regex(/^[0-9]{5}$/, { error: 'Eine Postleitzahl hat fünf Ziffern.' }).optional(),
  city: text(80).optional(),
  customerType: z.enum(LEAD_CUSTOMER_TYPES, { error: 'Die Art ist private, business, event oder unknown.' }),
  segment: z.enum(['gastronomie', 'buero', 'verein', 'hotel', 'handel', 'sonstiges'], { error: 'Dieses Segment gibt es nicht.' }).optional(),
  interest: text(1000).min(1, { error: 'Das Anliegen fehlt.' }),
  contactConsent: text(10).min(1, { error: 'Die Einwilligung in den Rückruf fehlt.' }),
  source: z.literal('phone_assistant', { error: 'Die Quelle ist phone_assistant.' }),
  occurredAt: isoInstant,
  callRef: text(64).optional(),
  locationCode: text(20).optional(),
})
export type LeadPayload = z.output<typeof zLeadPayload>

/** Kennung eines Interessenten von Vocaris (Spezifikation §7.1) */
export const LEAD_CASE_ID = /^voc-int-[0-9a-f]{32}$/

/** Interessenten ohne Kundenanlage werden nach so vielen Tagen geleert (Standard der Frist am Zugang, TA-R22) */
export const LEAD_RETENTION_DAYS = 180

export const SEGMENT_LABELS: Record<string, string> = {
  gastronomie: 'Gastronomie',
  buero: 'Büro',
  verein: 'Verein',
  hotel: 'Hotel',
  handel: 'Handel',
  sonstiges: 'Sonstiges',
}

export const segmentLabel = (segment: string | undefined): string | null =>
  segment ? (SEGMENT_LABELS[segment] ?? segment) : null

/** Kurzer Titel für Liste und Aufgabe. Löschanträge nennen keine Person (S9): Die Aufgabe bleibt auch nach dem Leeren harmlos. */
export function caseTitle(kind: CaseKind, payload: Record<string, unknown>): string {
  const company = typeof payload.companyName === 'string' ? payload.companyName : ''
  switch (kind) {
    case 'business_account':
      return company ? `Geschäftskunden-Antrag: ${company}` : 'Geschäftskunden-Antrag der Bestell-App'
    case 'account_deletion':
      return 'Löschantrag eines Kontos der Bestell-App'
    case 'lead':
      // keine Person im Titel (TA-R17): Liste und Aufgabe bleiben auch nach dem Leeren harmlos
      return 'Interessent aus dem Telefonassistenten'
    case 'mail': {
      const subject = typeof payload.subject === 'string' ? payload.subject.trim() : ''
      return subject ? `E-Mail: ${subject}` : 'E-Mail ohne Betreff'
    }
    default:
      return CASE_KIND_LABELS[kind]
  }
}

// ---- Abos (S6) ------------------------------------------------------------------------------------------------------------------

export const SUBSCRIPTION_RHYTHMS = ['weekly', 'biweekly', 'monthly'] as const
export const RHYTHM_LABELS: Record<(typeof SUBSCRIPTION_RHYTHMS)[number], string> = {
  weekly: 'wöchentlich',
  biweekly: 'alle 2 Wochen',
  monthly: 'monatlich',
}
/** Wochentag der App: 0 = Sonntag … 6 = Samstag (7 wird wie Sonntag gelesen) */
export const WEEKDAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'] as const
export const weekdayName = (weekday: number): string => WEEKDAY_NAMES[weekday % 7] ?? '–'

/** „wöchentlich, montags“, „alle 2 Wochen, montags“, „monatlich, montags“ – nur zur Anzeige */
export function rhythmText(rhythm: string, weekday: number): string {
  const day = weekdayName(weekday).toLowerCase()
  if (rhythm === 'weekly') return `wöchentlich, ${day}s`
  if (rhythm === 'biweekly') return `alle 2 Wochen, ${day}s`
  if (rhythm === 'monthly') return `monatlich, ${day}s`
  return day
}

// ---- Datenschutz (S9) ---------------------------------------------------------------------------------------------------------------

/** Abgeschlossene Löschanträge werden nach so vielen Tagen bis auf Art, Zeitpunkte und Ergebnis geleert. */
export const DELETION_RETENTION_DAYS = 30

/** Stichtag: Anträge, die an oder vor diesem Tag abgeschlossen wurden, sind zu leeren. */
export function deletionCutoff(now: Date): Date {
  return new Date(now.getTime() - DELETION_RETENTION_DAYS * 86_400_000)
}

/** Tag, an dem ein am `doneDay` abgeschlossener Löschantrag geleert wird (Anzeige) */
export const deletionClearDay = (doneDay: IsoDate): IsoDate => addDays(doneDay, DELETION_RETENTION_DAYS)

// ---- Einwilligungen (S8) --------------------------------------------------------------------------------------------------------------

/** „Werbe-Mitteilungen: eingewilligt seit 12.10.2026“ / „widerrufen am …“ / „keine Einwilligung“ – das Datum setzt die Oberfläche ein. */
export function offersPushState(
  consent: { granted: boolean | null } | null,
): 'granted' | 'revoked' | 'never' | 'unknown' {
  if (!consent) return 'unknown'
  if (consent.granted === null) return 'never'
  return consent.granted ? 'granted' : 'revoked'
}
